import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  Plus, Copy, Link2, ChevronDown, ChevronLeft, ChevronRight,
  X, Check, Trash2, Clock, MapPin, LayoutList, CalendarDays, Lock, Video,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { TimeDropdown } from '@/components/TimeDropdown';
import { useAuth } from '@/contexts/AuthContext';
import { DateInput } from '@/components/ui/DateInput';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SelectField } from '@/components/ui/SelectField';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AvailEvent {
  id: string;
  userId?: string;
  name: string;
  slug: string;
  duration: number;
  bufferBefore: number;
  bufferAfter: number;
  description?: string;
  location?: string;
  meetingLink?: string;
  meetingProvider?: string;
  maxPerDay: number;
  advanceNotice: number;
  futureLimit: number;
  color: string;
  isActive: boolean;
  createdBy?: string;
  isOwnerEvent?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  // Owner display name — surfaced when the viewer didn't create the
  // event themselves. Used to render "by <name>" on manager / downstream
  // shared events so people can tell whose calendar the slot belongs to.
  ownerName?: string | null;
}

interface DayHours {
  enabled: boolean;
  ranges: { start: string; end: string }[];
}

interface DateOverride {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  reason?: string;
}

interface BookingItem {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  status: string;
  guestName: string;
  guestEmail: string;
  notes?: string;
  meetingLink?: string | null;
  event: { name: string; duration: number; color: string; bufferAfter: number; bufferBefore: number; meetingProvider?: string | null };
  lead?: { id: string; name: string } | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#EAB308', '#8B5CF6', '#EC4899', '#F97316'];
const COLOR_NAMES: Record<string, string> = {
  '#3B82F6': 'Blue', '#EF4444': 'Red', '#22C55E': 'Green',
  '#EAB308': 'Yellow', '#8B5CF6': 'Purple', '#EC4899': 'Pink', '#F97316': 'Orange',
};
const DURATIONS = [15, 30, 45, 60, 90, 120];
const BUFFERS = [0, 5, 10, 15, 20];
const LOCATIONS = ['Google Meet', 'Zoom', 'Phone', 'In Person', 'Custom Link'];
const ADVANCE_OPTIONS = [
  { label: '30 min', value: 30 }, { label: '1 hr', value: 60 },
  { label: '2 hr', value: 120 }, { label: '4 hr', value: 240 }, { label: '24 hr', value: 1440 },
];
const FUTURE_OPTIONS = [
  { label: '7 days', value: 7 }, { label: '14 days', value: 14 },
  { label: '30 days', value: 30 }, { label: '60 days', value: 60 },
  { label: '90 days', value: 90 }, { label: 'Unlimited', value: 0 },
];
const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const DEFAULT_WEEKLY: DayHours[] = DAY_NAMES.map((_, i) => ({
  enabled: i >= 1 && i <= 5,
  ranges: i >= 1 && i <= 5 ? [{ start: '09:00', end: '18:00' }] : [],
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeDiff(start: string, end: string): string {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

function totalDayTime(ranges: { start: string; end: string }[]): string {
  let total = 0;
  ranges.forEach(r => {
    const [sh, sm] = r.start.split(':').map(Number);
    const [eh, em] = r.end.split(':').map(Number);
    total += (eh * 60 + em) - (sh * 60 + sm);
  });
  if (total <= 0) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

function formatTime12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getBookingUrl(userId: string, slug: string): string {
  return `${window.location.origin}/book/${userId}/${slug}`;
}

// ─── Validation helpers ───────────────────────────────────────────────────────

function toMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function addOneHour(t: string): string {
  const mins = Math.min(toMins(t) + 60, 23 * 60);
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

function getRangeError(start: string, end: string): string | null {
  const s = toMins(start); const e = toMins(end);
  if (e <= s) return 'End time must be after start time';
  if (e - s < 15) return 'Minimum 15 minutes required';
  return null;
}

function getOverlapError(ranges: { start: string; end: string }[], idx: number): string | null {
  const s = toMins(ranges[idx].start); const e = toMins(ranges[idx].end);
  for (let i = 0; i < ranges.length; i++) {
    if (i === idx) continue;
    if (s < toMins(ranges[i].end) && e > toMins(ranges[i].start)) return 'Time ranges cannot overlap';
  }
  return null;
}

function dayHasError(day: DayHours): boolean {
  if (!day.enabled) return false;
  return day.ranges.some((r, ri) =>
    !!getRangeError(r.start, r.end) || !!getOverlapError(day.ranges, ri)
  );
}

function weeklyHasError(weekly: DayHours[]): boolean {
  return weekly.some(dayHasError);
}

function weeklyHoursToAvailSettings(weekly: DayHours[]) {
  const workingDays = weekly.map((d, i) => d.enabled ? i : -1).filter(i => i >= 0);

  // Per-day ranges map: { "1": [{start, end}], "5": [{start, end}], ... }
  const weeklyHours: Record<string, { start: string; end: string }[]> = {};
  weekly.forEach((d, i) => {
    if (d.enabled && d.ranges.length > 0) weeklyHours[String(i)] = d.ranges;
  });

  // Global start/end: earliest start across all enabled days, latest end
  const allRanges = weekly.filter(d => d.enabled && d.ranges.length > 0).flatMap(d => d.ranges);
  const startTime = allRanges.length > 0
    ? allRanges.reduce((min, r) => r.start < min ? r.start : min, allRanges[0].start)
    : '09:00';
  const endTime = allRanges.length > 0
    ? allRanges.reduce((max, r) => r.end > max ? r.end : max, allRanges[0].end)
    : '18:00';

  return { workingDays, startTime, endTime, weeklyHours };
}

// ─── Event Type Drawer ────────────────────────────────────────────────────────

function EventDrawer({
  event,
  onClose,
  onSave,
  onDelete,
  isAdminView,
  isOwnerOrAdmin,
  readOnly,
}: {
  event: Partial<AvailEvent> | null;
  onClose: () => void;
  onSave: (data: any) => void;
  onDelete?: () => void;
  isAdminView?: boolean;
  isOwnerOrAdmin?: boolean;
  readOnly?: boolean;
}) {
  const [form, setForm] = useState({
    name: event?.name ?? '',
    color: event?.color ?? '#3B82F6',
    duration: event?.duration ?? 30,
    bufferAfter: event?.bufferAfter ?? 0,
    location: event?.location ?? '',
    meetingLink: event?.meetingLink ?? '',
    meetingProvider: event?.meetingProvider ?? '',
    description: event?.description ?? '',
    maxPerDay: event?.maxPerDay ?? 8,
    advanceNotice: event?.advanceNotice ?? 60,
    futureLimit: event?.futureLimit ?? 0, // new events default to Unlimited; existing events keep their saved value
    isActive: event?.isActive ?? true,
    isOwnerEvent: event?.isOwnerEvent ?? false,
  });

  const [googleStatus, setGoogleStatus] = useState<{ connected: boolean; email?: string; status?: 'connected' | 'token_expired' }>({ connected: false });
  const [zoomStatus, setZoomStatus] = useState<{ connected: boolean; email?: string; status?: 'connected' | 'token_expired' }>({ connected: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testingProvider, setTestingProvider] = useState<'google' | 'zoom' | null>(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState<'google' | 'zoom' | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Fetch integration connection status on mount
  useEffect(() => {
    Promise.all([
      api.get('/availability/integrations/google/status').then(r => setGoogleStatus(r.data)).catch(() => {}),
      api.get('/availability/integrations/zoom/status').then(r => setZoomStatus(r.data)).catch(() => {}),
    ]).finally(() => setLoadingStatus(false));
  }, []);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleLocationChange = (loc: string) => {
    set('location', loc);
    // Reset meeting provider when location changes
    if (loc === 'Google Meet') {
      set('meetingProvider', googleStatus.connected ? 'google_meet_auto' : '');
    } else if (loc === 'Zoom') {
      set('meetingProvider', zoomStatus.connected ? 'zoom_auto' : '');
    } else {
      set('meetingProvider', '');
    }
    if (loc !== 'Custom Link') set('meetingLink', '');
  };

  const handleConnect = async (provider: 'google' | 'zoom') => {
    try {
      const { data } = await api.get(`/availability/integrations/${provider}/connect`);
      if (data.url) {
        window.open(data.url, '_blank', 'width=600,height=700');
      } else if (data.error) {
        toast('error', data.error);
      }
    } catch {
      toast('error', `Failed to start ${provider === 'google' ? 'Google' : 'Zoom'} connection`);
    }
  };

  // I7 — Open confirmation dialog instead of disconnecting immediately.
  const handleDisconnect = (provider: 'google' | 'zoom') => {
    setDisconnectConfirm(provider);
  };

  const confirmDisconnect = async () => {
    if (!disconnectConfirm) return;
    const provider = disconnectConfirm;
    setDisconnecting(true);
    try {
      await api.delete(`/availability/integrations/${provider}/disconnect`);
      if (provider === 'google') {
        setGoogleStatus({ connected: false });
        if (form.meetingProvider === 'google_meet_auto') set('meetingProvider', '');
      } else {
        setZoomStatus({ connected: false });
        if (form.meetingProvider === 'zoom_auto') set('meetingProvider', '');
      }
      toast('success', `${provider === 'google' ? 'Google Calendar' : 'Zoom'} disconnected`);
      setDisconnectConfirm(null);
    } catch {
      toast('error', 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  // I5 — Test Connection: hit backend probe endpoint, show toast.
  const handleTest = async (provider: 'google' | 'zoom') => {
    setTestingProvider(provider);
    try {
      const { data } = await api.post(`/availability/integrations/${provider}/test`);
      if (data?.ok) {
        toast('success', `Connection OK${data.email ? ` (${data.email})` : ''}`);
      } else {
        toast('error', data?.error || 'Connection test failed');
      }
    } catch (err: any) {
      toast('error', err?.response?.data?.error || 'Connection test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  const isGoogleMeet = form.location === 'Google Meet';
  const isZoom = form.location === 'Zoom';
  const isCustomLink = form.location === 'Custom Link';
  const showMeetingConfig = isGoogleMeet || isZoom;
  const isEdit = !!event?.id;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">{readOnly ? 'View Event Type' : isEdit ? 'Edit Event Type' : 'New Event Type'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className={`flex-1 overflow-y-auto px-6 py-5 space-y-5 ${readOnly ? 'pointer-events-none opacity-70' : ''}`}>
          {/* Name */}
          <div>
            <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Event Name</label>
            <input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. 30 Min Discovery Call"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Color */}
          <div>
            <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Color</label>
            <div className="flex items-center gap-2 mt-2">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => set('color', c)}
                  title={COLOR_NAMES[c]}
                  className="w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? '#1E293B' : 'transparent',
                    transform: form.color === c ? 'scale(1.15)' : 'scale(1)',
                  }}
                >
                  {form.color === c && <Check className="h-4 w-4 text-white" />}
                </button>
              ))}
            </div>
          </div>

          {/* Duration + Buffer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Duration</label>
              <SelectField value={form.duration} onChange={e => set('duration', +e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Buffer Time</label>
              <SelectField value={form.bufferAfter} onChange={e => set('bufferAfter', +e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {BUFFERS.map(b => <option key={b} value={b}>{b} min</option>)}
              </SelectField>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Location</label>
            <SelectField value={form.location} onChange={e => handleLocationChange(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Select location</option>
              {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
            </SelectField>
          </div>

          {/* ── Meeting Link Configuration (Google Meet / Zoom) ─────────── */}
          {showMeetingConfig && !loadingStatus && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
              {/* Connection status — 3 states:
                   connected (green dot + Disconnect),
                   token_expired (amber warning + Reconnect),
                   not connected (grey dot + Connect) */}
              {isGoogleMeet && (() => {
                const expired = googleStatus.status === 'token_expired';
                return (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          googleStatus.connected ? 'bg-emerald-500'
                          : expired ? 'bg-amber-500'
                          : 'bg-slate-300'
                        }`} />
                        <span className="text-xs font-medium text-slate-600">
                          {expired
                            ? `Google Calendar — Reconnect required${googleStatus.email ? ` (${googleStatus.email})` : ''}`
                            : googleStatus.connected
                              ? `Google Calendar (${googleStatus.email || 'Connected'})`
                              : 'Google Calendar — Not Connected'}
                        </span>
                      </div>
                      {googleStatus.connected ? (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleTest('google')}
                            disabled={testingProvider === 'google'}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                            {testingProvider === 'google' ? 'Testing…' : 'Test'}
                          </button>
                          <button onClick={() => handleDisconnect('google')}
                            className="text-xs text-red-500 hover:text-red-700 font-medium">
                            Disconnect
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleConnect('google')}
                          className={`text-xs font-semibold ${expired ? 'text-amber-600 hover:text-amber-800' : 'text-blue-600 hover:text-blue-800'}`}>
                          {expired ? 'Reconnect' : 'Connect'}
                        </button>
                      )}
                    </div>
                    {expired && (
                      <p className="text-xs text-amber-600 mt-1.5">
                        Token expired or access was revoked. New bookings won't get auto-generated Meet links until you reconnect.
                      </p>
                    )}
                  </div>
                );
              })()}

              {isZoom && (() => {
                const expired = zoomStatus.status === 'token_expired';
                return (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          zoomStatus.connected ? 'bg-emerald-500'
                          : expired ? 'bg-amber-500'
                          : 'bg-slate-300'
                        }`} />
                        <span className="text-xs font-medium text-slate-600">
                          {expired
                            ? `Zoom — Reconnect required${zoomStatus.email ? ` (${zoomStatus.email})` : ''}`
                            : zoomStatus.connected
                              ? `Zoom (${zoomStatus.email || 'Connected'})`
                              : 'Zoom — Not Connected'}
                        </span>
                      </div>
                      {zoomStatus.connected ? (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleTest('zoom')}
                            disabled={testingProvider === 'zoom'}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                            {testingProvider === 'zoom' ? 'Testing…' : 'Test'}
                          </button>
                          <button onClick={() => handleDisconnect('zoom')}
                            className="text-xs text-red-500 hover:text-red-700 font-medium">
                            Disconnect
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleConnect('zoom')}
                          className={`text-xs font-semibold ${expired ? 'text-amber-600 hover:text-amber-800' : 'text-blue-600 hover:text-blue-800'}`}>
                          {expired ? 'Reconnect' : 'Connect'}
                        </button>
                      )}
                    </div>
                    {expired && (
                      <p className="text-xs text-amber-600 mt-1.5">
                        Token expired or access was revoked. New bookings won't get auto-generated Zoom links until you reconnect.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Link mode selection */}
              <div className="space-y-2 pt-1">
                <label className="text-xs font-normal text-slate-500 uppercase tracking-wide">Meeting Link Mode</label>

                {/* Auto-generate option */}
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  form.meetingProvider === (isGoogleMeet ? 'google_meet_auto' : 'zoom_auto')
                    ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 hover:bg-white'
                }`}>
                  <input
                    type="radio"
                    name="linkMode"
                    checked={form.meetingProvider === (isGoogleMeet ? 'google_meet_auto' : 'zoom_auto')}
                    onChange={() => set('meetingProvider', isGoogleMeet ? 'google_meet_auto' : 'zoom_auto')}
                    disabled={isGoogleMeet ? !googleStatus.connected : !zoomStatus.connected}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Auto-generate unique link</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      A new {isGoogleMeet ? 'Google Meet' : 'Zoom'} link will be created for each booking automatically
                    </p>
                    {(isGoogleMeet ? !googleStatus.connected : !zoomStatus.connected) && (
                      <p className="text-xs text-amber-600 mt-1">Connect {isGoogleMeet ? 'Google Calendar' : 'Zoom'} above to enable</p>
                    )}
                  </div>
                </label>

                {/* Fixed link option */}
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  form.meetingProvider !== (isGoogleMeet ? 'google_meet_auto' : 'zoom_auto')
                    ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 hover:bg-white'
                }`}>
                  <input
                    type="radio"
                    name="linkMode"
                    checked={form.meetingProvider !== (isGoogleMeet ? 'google_meet_auto' : 'zoom_auto')}
                    onChange={() => set('meetingProvider', '')}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">Use fixed link</p>
                    <p className="text-xs text-slate-400 mt-0.5">Same link for every booking</p>
                  </div>
                </label>

                {/* Fixed link input */}
                {form.meetingProvider !== (isGoogleMeet ? 'google_meet_auto' : 'zoom_auto') && (
                  <input
                    value={form.meetingLink}
                    onChange={e => set('meetingLink', e.target.value)}
                    placeholder={isGoogleMeet ? 'https://meet.google.com/...' : 'https://zoom.us/j/...'}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                )}
              </div>
            </div>
          )}

          {/* Custom Link input */}
          {isCustomLink && (
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Meeting Link</label>
              <input
                value={form.meetingLink}
                onChange={e => set('meetingLink', e.target.value)}
                placeholder="https://your-meeting-url.com/..."
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              placeholder="What is this event about?"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>

          {/* Max bookings */}
          <div>
            <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Max Bookings per Day</label>
            <input type="number" min={1} max={50} value={form.maxPerDay}
              onChange={e => set('maxPerDay', +e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          {/* Advance notice + Future limit */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Advance Notice</label>
              <SelectField value={form.advanceNotice} onChange={e => set('advanceNotice', +e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {ADVANCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 uppercase tracking-wide">Booking Window</label>
              <SelectField value={form.futureLimit} onChange={e => set('futureLimit', +e.target.value)}
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {FUTURE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <div>
              <p className="text-sm font-medium text-slate-800">Active</p>
              <p className="text-xs text-slate-400">Accept new bookings for this event</p>
            </div>
            <button
              onClick={() => set('isActive', !form.isActive)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.isActive ? 'bg-blue-600' : 'bg-slate-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.isActive ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Owner event toggle — admin/owner */}
          {(isAdminView || isOwnerOrAdmin) && (
            <div className="flex items-center justify-between py-2 border-t border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-800">Owner Event</p>
                <p className="text-xs text-slate-400">Only the counselor can delete owner events</p>
              </div>
              <button
                onClick={() => set('isOwnerEvent', !form.isOwnerEvent)}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.isOwnerEvent ? 'bg-amber-500' : 'bg-slate-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.isOwnerEvent ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3">
          {!readOnly && isEdit && onDelete && event?.canDelete !== false && (
            <button onClick={onDelete}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg border border-red-200">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              onClick={() => onSave(form)}
              disabled={!form.name.trim()}
              className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isEdit ? 'Save Changes' : 'Create Event'}
            </button>
          )}
        </div>
      </div>

      {/* I7 — Disconnect confirmation dialog */}
      <ConfirmModal
        isOpen={!!disconnectConfirm}
        title={`Disconnect ${disconnectConfirm === 'google' ? 'Google Calendar' : 'Zoom'}?`}
        message={
          disconnectConfirm === 'google'
            ? "Future bookings won't get auto-generated Google Meet links or calendar sync until you reconnect. Your saved Client ID and Secret will be kept."
            : "Future bookings won't get auto-generated Zoom links until you reconnect. Your saved Client ID and Secret will be kept."
        }
        confirmText="Disconnect"
        confirmButtonColor="red"
        onConfirm={confirmDisconnect}
        onCancel={() => setDisconnectConfirm(null)}
        isLoading={disconnecting}
      />
    </div>
  );
}

// ─── Event Card ───────────────────────────────────────────────────────────────

function EventCard({
  event,
  userId,
  currentUserId,
  onClick,
  onToggle,
  onCopyLink,
  isAdminView,
}: {
  event: AvailEvent;
  userId: string;
  currentUserId: string;
  onClick: () => void;
  onToggle: () => void;
  onCopyLink: () => void;
  isAdminView?: boolean;
}) {
  // Owner events are read-only for non-owner users
  const isReadOnly = !!(event.isOwnerEvent && event.userId !== currentUserId);

  return (
    <div
      onClick={isReadOnly ? undefined : onClick}
      className={`flex-shrink-0 w-56 bg-white rounded-xl border border-slate-200 transition-shadow overflow-hidden ${isReadOnly ? 'opacity-80' : 'cursor-pointer hover:shadow-md'}`}
    >
      {/* Color bar */}
      <div className="h-1.5 w-full" style={{ backgroundColor: event.color }} />

      <div className="p-4">
        {/* Top row: toggle + lock/copy */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={e => { e.stopPropagation(); if (!isReadOnly) onToggle(); }}
            disabled={isReadOnly}
            className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${event.isActive ? 'bg-blue-600' : 'bg-slate-200'} ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${event.isActive ? 'left-5' : 'left-0.5'}`} />
          </button>
          <div className="flex items-center gap-1">
            {event.isOwnerEvent && (
              <span title="Owner-locked: only the counselor can delete this event" className="p-1.5 text-amber-500">
                <Lock className="h-3.5 w-3.5" />
              </span>
            )}
            <button
              onClick={e => { e.stopPropagation(); onCopyLink(); }}
              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
              title="Copy booking link"
            >
              <Link2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Name */}
        <p className="text-sm font-bold text-slate-900 leading-tight mb-1 line-clamp-2">{event.name}</p>

        {/* Owner attribution — only when the event was created by someone
            other than the current user (e.g. shared upline / manager /
            admin event visible via the hierarchy rule). Reads "by <name>". */}
        {event.ownerName && event.userId && event.userId !== currentUserId && (
          <p className="text-[11px] text-slate-400 mb-3">
            by <span className="font-medium text-slate-500">{event.ownerName}</span>
          </p>
        )}
        {!(event.ownerName && event.userId && event.userId !== currentUserId) && (
          <div className="mb-3" />
        )}

        {/* Meta */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Duration: {event.duration} min</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Buffer: {event.bufferAfter} min</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Time Input ───────────────────────────────────────────────────────────────

function TimeInput({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: boolean }) {
  return (
    <div className={error ? 'rounded-lg ring-1 ring-red-400' : ''}>
      <TimeDropdown value={value} onChange={onChange} className="w-28" />
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────

function ListView({
  weekly,
  setWeekly,
  overrides,
  onAddOverride,
  onRemoveOverride,
  onSaveWeekly,
  saving,
}: {
  weekly: DayHours[];
  setWeekly: React.Dispatch<React.SetStateAction<DayHours[]>>;
  overrides: DateOverride[];
  onAddOverride: (dto: { date: string; timeRanges: { start: string; end: string }[]; reason?: string }) => void;
  onRemoveOverride: (id: string) => void;
  onSaveWeekly: () => void;
  saving: boolean;
}) {
  const [overrideForm, setOverrideForm] = useState({
    date: '',
    timeRanges: [{ start: '09:00', end: '18:00' }],
    reason: '',
  });
  const [showOverrideForm, setShowOverrideForm] = useState(false);

  const updateOverrideRange = (ri: number, patch: Partial<{ start: string; end: string }>) =>
    setOverrideForm(f => ({ ...f, timeRanges: f.timeRanges.map((r, i) => i === ri ? { ...r, ...patch } : r) }));

  const addOverrideRange = () =>
    setOverrideForm(f => {
      const lastEnd = f.timeRanges[f.timeRanges.length - 1]?.end ?? '09:00';
      return { ...f, timeRanges: [...f.timeRanges, { start: lastEnd, end: '18:00' }] };
    });

  const removeOverrideRange = (ri: number) =>
    setOverrideForm(f => ({ ...f, timeRanges: f.timeRanges.filter((_, i) => i !== ri) }));

  const updateDay = (i: number, patch: Partial<DayHours>) =>
    setWeekly(w => w.map((d, idx) => idx === i ? { ...d, ...patch } : d));

  const updateRange = (dayIdx: number, rangeIdx: number, patch: Partial<{ start: string; end: string }>) =>
    setWeekly(w => w.map((d, i) => i === dayIdx
      ? { ...d, ranges: d.ranges.map((r, ri) => ri === rangeIdx ? { ...r, ...patch } : r) }
      : d));

  const addRange = (dayIdx: number) =>
    setWeekly(w => w.map((d, i) => {
      if (i !== dayIdx) return d;
      const lastEnd = d.ranges[d.ranges.length - 1]?.end ?? '09:00';
      return { ...d, ranges: [...d.ranges, { start: lastEnd, end: '18:00' }] };
    }));

  const removeRange = (dayIdx: number, rangeIdx: number) =>
    setWeekly(w => w.map((d, i) => i === dayIdx
      ? { ...d, ranges: d.ranges.filter((_, ri) => ri !== rangeIdx) }
      : d));

  // ── Copy dropdown ────────────────────────────────────────────────────────────

  const [copyPopup, setCopyPopup] = useState<{
    dayIdx: number;
    anchorRect: DOMRect;
    selected: number[];
  } | null>(null);

  const openCopyDropdown = (e: React.MouseEvent, dayIdx: number) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setCopyPopup({ dayIdx, anchorRect: rect, selected: [] });
  };

  const applyCopy = () => {
    if (!copyPopup) return;
    const { dayIdx, selected } = copyPopup;
    if (selected.length === 0) { setCopyPopup(null); return; }
    const ranges = weekly[dayIdx].ranges;
    setWeekly(w => w.map((d, i) =>
      selected.includes(i) ? { ...d, enabled: true, ranges: ranges.map(r => ({ ...r })) } : d
    ));
    const names = selected.map(i => DAY_NAMES[i]).join(', ');
    toast('success', `Times copied to ${names}`);
    setCopyPopup(null);
  };

  // Auto-fix: if new end <= start, set end to start + 1hr and toast
  const handleEndChange = (dayIdx: number, rangeIdx: number, start: string, newEnd: string) => {
    if (toMins(newEnd) <= toMins(start)) {
      const fixed = addOneHour(start);
      updateRange(dayIdx, rangeIdx, { end: fixed });
      toast('success', `End time adjusted to ${formatTime12(fixed)}`);
    } else {
      updateRange(dayIdx, rangeIdx, { end: newEnd });
    }
  };

  const hasErrors = weeklyHasError(weekly);

  return (
    <div className="flex gap-6">
      {/* Weekly hours (60%) */}
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[auto_60px_1fr_80px_60px] gap-0 border-b border-slate-100 px-4 py-2.5 bg-slate-50">
            <div className="w-5" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Day</p>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Hours</p>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Total</p>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">Action</p>
          </div>

          {weekly.map((day, di) => (
            <div key={di} className={`grid grid-cols-[auto_60px_1fr_80px_60px] gap-0 px-4 py-3 border-b border-slate-50 last:border-0 ${!day.enabled ? 'bg-slate-50/50' : ''}`}>
              {/* Checkbox */}
              <div className="flex items-start pt-0.5 w-5">
                <button
                  onClick={() => updateDay(di, {
                    enabled: !day.enabled,
                    ranges: !day.enabled && day.ranges.length === 0 ? [{ start: '09:00', end: '18:00' }] : day.ranges,
                  })}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${day.enabled ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}
                >
                  {day.enabled && <Check className="h-2.5 w-2.5 text-white" />}
                </button>
              </div>

              {/* Day name */}
              <div className="flex items-start pt-0.5">
                <span className={`text-xs font-bold ${day.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                  {DAY_NAMES[di]}
                </span>
              </div>

              {/* Ranges */}
              <div className="space-y-1.5">
                {!day.enabled ? (
                  <span className="text-xs text-slate-400 italic py-0.5">Unavailable</span>
                ) : (
                  <>
                    {day.ranges.map((r, ri) => {
                      const rangeErr = getRangeError(r.start, r.end);
                      const overlapErr = getOverlapError(day.ranges, ri);
                      const rowErr = rangeErr || overlapErr;
                      return (
                        <div key={ri}>
                          <div className="flex items-center gap-1.5">
                            <TimeInput value={r.start} onChange={v => updateRange(di, ri, { start: v })} error={!!rowErr} />
                            <span className="text-slate-400 text-xs">–</span>
                            <TimeInput value={r.end} onChange={v => handleEndChange(di, ri, r.start, v)} error={!!rowErr} />
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium min-w-[40px] text-center ${rowErr ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-400'}`}>
                              {rowErr ? 'Invalid' : timeDiff(r.start, r.end)}
                            </span>
                            {day.ranges.length > 1 && (
                              <button onClick={() => removeRange(di, ri)} className="text-slate-300 hover:text-red-500">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          {rowErr && (
                            <p className="text-[11px] text-red-500 mt-0.5 ml-0.5">{rowErr}</p>
                          )}
                        </div>
                      );
                    })}
                    <button
                      onClick={() => addRange(di)}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-0.5"
                    >
                      <Plus className="h-3 w-3" /> Add time range
                    </button>
                  </>
                )}
              </div>

              {/* Total */}
              <div className="flex items-start justify-end pt-0.5">
                {day.enabled && day.ranges.length > 0 ? (
                  dayHasError(day)
                    ? <span className="text-xs font-medium text-red-500">Invalid</span>
                    : <span className="text-xs font-medium text-slate-600">{totalDayTime(day.ranges)}</span>
                ) : (
                  <span className="text-xs font-medium text-slate-400">—</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-start justify-center pt-0.5">
                {day.enabled && (
                  <button
                    onClick={e => openCopyDropdown(e, di)}
                    title="Copy times to other days"
                    className={`p-1 rounded transition-colors ${copyPopup?.dayIdx === di ? 'text-emerald-700 bg-emerald-100' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-3">
          <button
            onClick={onSaveWeekly}
            disabled={saving || hasErrors}
            title={hasErrors ? 'Fix time range errors before saving' : undefined}
            className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Availability'}
          </button>
        </div>
      </div>

      {/* Date overrides */}
      <div className="w-[380px] flex-shrink-0">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-slate-800">Specific Date</p>
            <button
              onClick={() => setShowOverrideForm(s => !s)}
              className="text-xs font-medium text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50"
            >
              + Add date-specific hours
            </button>
          </div>

          {showOverrideForm && (
            <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3 border border-slate-100">
              <div>
                <label className="text-xs font-normal text-slate-600">Date</label>
                <DateInput className="mt-1 w-full" value={overrideForm.date}
                  onChange={v => setOverrideForm(f => ({ ...f, date: v }))} />
              </div>

              {/* Time ranges */}
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-1.5 items-center">
                  <span className="text-xs font-medium text-slate-600">Start</span>
                  <span />
                  <span className="text-xs font-medium text-slate-600">End</span>
                  <span />
                </div>
                {overrideForm.timeRanges.map((r, ri) => {
                  const rangeErr = getRangeError(r.start, r.end);
                  const overlapErr = getOverlapError(overrideForm.timeRanges, ri);
                  const rowErr = rangeErr || overlapErr;
                  return (
                    <div key={ri}>
                      <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-1.5 items-center">
                        <TimeDropdown value={r.start} onChange={v => updateOverrideRange(ri, { start: v })} />
                        <span className="text-slate-400 text-xs">–</span>
                        <TimeDropdown value={r.end} onChange={v => {
                          if (toMins(v) <= toMins(r.start)) {
                            const fixed = addOneHour(r.start);
                            updateOverrideRange(ri, { end: fixed });
                            toast('success', `End time adjusted to ${formatTime12(fixed)}`);
                          } else {
                            updateOverrideRange(ri, { end: v });
                          }
                        }} />
                        {overrideForm.timeRanges.length > 1 ? (
                          <button onClick={() => removeOverrideRange(ri)} className="text-slate-300 hover:text-red-500">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        ) : <span className="w-3.5" />}
                      </div>
                      {rowErr && <p className="text-[11px] text-red-500 mt-0.5 ml-0.5">{rowErr}</p>}
                    </div>
                  );
                })}
                <button
                  onClick={addOverrideRange}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Add time range
                </button>
              </div>

              <input value={overrideForm.reason}
                onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Reason (optional)"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (!overrideForm.date) return;
                    const hasErr = overrideForm.timeRanges.some((r, ri) =>
                      !!getRangeError(r.start, r.end) || !!getOverlapError(overrideForm.timeRanges, ri)
                    );
                    if (hasErr) { toast('error', 'Please fix time range errors before saving'); return; }
                    onAddOverride({ date: overrideForm.date, timeRanges: overrideForm.timeRanges, reason: overrideForm.reason || undefined });
                    setOverrideForm({ date: '', timeRanges: [{ start: '09:00', end: '18:00' }], reason: '' });
                    setShowOverrideForm(false);
                  }}
                  className="flex-1 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
                <button onClick={() => setShowOverrideForm(false)} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {overrides.length === 0 && !showOverrideForm ? (
            <p className="text-sm text-slate-400 text-center py-8">No date-specific hours set</p>
          ) : (() => {
            // Group overrides by date, filter to current month start or later
            const now = new Date();
            const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            const grouped: Record<string, DateOverride[]> = {};
            overrides.forEach(o => {
              if (o.date >= currentMonthStart) (grouped[o.date] ??= []).push(o);
            });
            const sortedDates = Object.keys(grouped).sort();

            return (
              <div className="space-y-2">
                {sortedDates.map(date => {
                  const rows = grouped[date];
                  const d = new Date(date + 'T00:00:00');
                  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                  const MOS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const label = `${DAYS[d.getDay()]}, ${MOS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

                  return (
                    <div key={date} className="border border-slate-200 rounded-xl overflow-hidden">
                      {/* Date header */}
                      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                        <div>
                          <span className="text-xs font-bold text-slate-800">{date}</span>
                          <span className="text-xs text-slate-500 ml-2">{label}</span>
                        </div>
                        <button
                          onClick={() => rows.forEach(r => onRemoveOverride(r.id))}
                          className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0"
                          title="Delete all ranges for this date"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Time ranges */}
                      <div className="divide-y divide-slate-100">
                        {rows.map(o => (
                          <div key={o.id} className="flex items-center justify-between px-3 py-2">
                            <div className="text-xs text-slate-700">
                              <span className="font-medium">{formatTime12(o.startTime)} – {formatTime12(o.endTime)}</span>
                              {o.reason && <span className="text-slate-500 ml-1.5">· {o.reason}</span>}
                            </div>
                            <button onClick={() => onRemoveOverride(o.id)} className="p-1 text-slate-300 hover:text-red-500 flex-shrink-0 ml-2">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Copy-times dropdown — fixed so it's never clipped by parent overflow */}
      {copyPopup && (() => {
        const { dayIdx, anchorRect, selected } = copyPopup;
        const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return (
          <>
            {/* invisible backdrop to close on outside click */}
            <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setCopyPopup(null)} />
            <div
              className="fixed bg-white rounded-xl shadow-2xl border border-slate-200 p-3"
              style={{
                zIndex: 9999,
                top: anchorRect.bottom + 6,
                right: window.innerWidth - anchorRect.right,
                width: 220,
              }}
              onClick={e => e.stopPropagation()}
            >
              <p className="text-xs text-slate-400 mb-2 font-medium">Copy times to..</p>
              <div className="space-y-0.5 mb-3">
                {DAY_FULL.map((name, i) => {
                  const isSelf = i === dayIdx;
                  const checked = isSelf || selected.includes(i);
                  return (
                    <label
                      key={i}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg ${isSelf ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isSelf}
                        onChange={() => {
                          if (isSelf) return;
                          setCopyPopup(p => p ? {
                            ...p,
                            selected: p.selected.includes(i)
                              ? p.selected.filter(x => x !== i)
                              : [...p.selected, i],
                          } : p);
                        }}
                        className="h-3.5 w-3.5 accent-blue-600 flex-shrink-0"
                      />
                      <span className="text-sm text-slate-700">{name}</span>
                    </label>
                  );
                })}
              </div>
              <button
                onClick={applyCopy}
                disabled={selected.length === 0}
                className="w-full py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </>
        );
      })()}
    </div>
  );
}

// ─── Calendar View ────────────────────────────────────────────────────────────

function CalendarView({
  userId,
  events,
  availData,
}: {
  userId: string;
  events: AvailEvent[];
  availData: any;
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [calMode, setCalMode] = useState<'availability' | 'booked'>('availability');
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [popup, setPopup] = useState<{ booking: BookingItem; x: number; y: number } | null>(null);
  // Tracks the in-flight regenerate call from the popup so we can disable
  // the button + swap the icon while the backend re-runs Zoom/Meet
  // generation.
  const [regenerating, setRegenerating] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const reloadBookings = useCallback(() => {
    api.get(`/availability/bookings?month=${monthStr}`).then(r => setBookings(r.data ?? [])).catch(() => {});
  }, [monthStr]);

  useEffect(() => { reloadBookings(); }, [reloadBookings]);

  const regenerateMeetingLink = async () => {
    if (!popup?.booking?.id) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/availability/bookings/${popup.booking.id}/regenerate-meeting-link`);
      if (data?.ok && data.meetingLink) {
        toast('success', 'Meeting link created');
        // Update the popup + the underlying bookings list so both surfaces
        // reflect the new link without a full page refresh.
        setPopup(p => p ? { ...p, booking: { ...p.booking, meetingLink: data.meetingLink } } : p);
        setBookings(prev => prev.map(b => b.id === popup.booking.id ? { ...b, meetingLink: data.meetingLink } : b));
      } else {
        toast('error', data?.error || 'Could not create meeting link');
      }
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Could not create meeting link');
    } finally {
      setRegenerating(false);
    }
  };

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build calendar cells
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const toDateStr = (d: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const bookingsByDate = bookings.reduce<Record<string, BookingItem[]>>((acc, b) => {
    (acc[b.date] ??= []).push(b); return acc;
  }, {});

  // Availability settings (current)
  const workingDays: number[] = availData?.workingDays ?? [1, 2, 3, 4, 5];
  const globalStart: string = availData?.startTime ?? '09:00';
  const globalEnd: string = availData?.endTime ?? '18:00';
  const weeklyHours: Record<string, { start: string; end: string }[]> = availData?.weeklyHours ?? {};
  const customOverrides: { date: string; startTime: string; endTime: string }[] = availData?.customOverrides ?? [];
  const blockedDates: { date: string }[] = availData?.blockedDates ?? [];

  // Snapshots sorted by effectiveFrom ascending (for history on past dates)
  const snapshots: { effectiveFrom: string; workingDays: number[]; startTime: string; endTime: string; weeklyHours: Record<string, { start: string; end: string }[]> }[] = availData?.snapshots ?? [];

  // Find the snapshot that was active on a given date (latest snapshot with effectiveFrom <= dateStr)
  const getSnapshotForDate = (dateStr: string) => {
    if (snapshots.length === 0) return null;
    let matched: (typeof snapshots)[0] | null = null;
    for (const snap of snapshots) {
      if (snap.effectiveFrom <= dateStr) matched = snap;
      else break;
    }
    return matched;
  };

  // Group overrides by date for quick lookup (handles multiple ranges per date)
  const overridesByDate: Record<string, { startTime: string; endTime: string }[]> = {};
  customOverrides.forEach(o => { (overridesByDate[o.date] ??= []).push(o); });

  const getAvailForDay = (d: number): { start: string; end: string; totalMins?: number } | null => {
    const ds = toDateStr(d);
    const dow = new Date(year, month, d).getDay();
    if (blockedDates.some(b => b.date === ds)) return null;

    // Check date-specific overrides (may have multiple ranges)
    const dateOverrides = overridesByDate[ds];
    if (dateOverrides?.length) {
      // Return earliest start and latest end, plus total minutes for badge
      let earliest = dateOverrides[0].startTime;
      let latest = dateOverrides[0].endTime;
      let totalMins = 0;
      dateOverrides.forEach(o => {
        if (o.startTime < earliest) earliest = o.startTime;
        if (o.endTime > latest) latest = o.endTime;
        const [sh, sm] = o.startTime.split(':').map(Number);
        const [eh, em] = o.endTime.split(':').map(Number);
        totalMins += (eh * 60 + em) - (sh * 60 + sm);
      });
      return { start: earliest, end: latest, totalMins };
    }

    // Use snapshot for the specific date if available (shows historical availability)
    const snap = getSnapshotForDate(ds);
    if (snap) {
      const wd: number[] = Array.isArray(snap.workingDays) ? snap.workingDays : [1, 2, 3, 4, 5];
      if (!wd.includes(dow)) return null;
      const wh = (snap.weeklyHours && typeof snap.weeklyHours === 'object') ? snap.weeklyHours : {};
      const dayRanges = wh[String(dow)];
      if (dayRanges?.length) return { start: dayRanges[0].start, end: dayRanges[dayRanges.length - 1].end };
      return { start: snap.startTime || '09:00', end: snap.endTime || '18:00' };
    }

    // Fallback to current settings (no snapshots yet)
    if (!workingDays.includes(dow)) return null;
    const dayRanges = weeklyHours[String(dow)];
    if (dayRanges?.length) return { start: dayRanges[0].start, end: dayRanges[dayRanges.length - 1].end };
    return { start: globalStart, end: globalEnd };
  };

  // Smart popup positioning: flip left if in right half of screen, flip up if near bottom
  const getPopupStyle = (x: number, y: number): React.CSSProperties => {
    const popupW = 280;
    const popupH = 240;
    const style: React.CSSProperties = {};
    if (x + popupW + 12 > window.innerWidth) {
      style.right = window.innerWidth - x + 4;
    } else {
      style.left = x + 8;
    }
    if (y + popupH + 12 > window.innerHeight) {
      style.bottom = window.innerHeight - y + 4;
    } else {
      style.top = y + 8;
    }
    return style;
  };

  const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
  const isPast = (d: number) => new Date(year, month, d) < todayMidnight;

  return (
    <div className="w-full" onClick={() => setPopup(null)}>
      {/* Calendar nav */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <span className="text-base font-bold text-slate-900 min-w-[160px] text-center">
            {MONTH_NAMES[month]} – {year}
          </span>
          <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCalMode('booked')}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors ${calMode === 'booked' ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            Booked Slots
          </button>
          <button
            onClick={() => setCalMode('availability')}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors ${calMode === 'availability' ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            Availability
          </button>
        </div>
      </div>

      {/* Sticky day headers */}
      <div className="sticky top-0 z-10 bg-white grid grid-cols-7 border border-b-0 border-slate-200 rounded-t-xl">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2.5">{d}</div>
        ))}
      </div>

      {/* Calendar grid — auto height, no overflow:hidden so page can scroll */}
      <div className="grid grid-cols-7 border-l border-b border-r border-slate-200 rounded-b-xl">
        {cells.map((d, i) => {
          if (!d) return (
            <div
              key={i}
              className={`min-h-[100px] bg-slate-50/60 border-r border-t border-slate-200 ${i % 7 === 6 ? 'border-r-0' : ''}`}
            />
          );

          const ds = toDateStr(d);
          const isToday = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
          const past = isPast(d);
          const dayBookings = bookingsByDate[ds] ?? [];
          const avail = getAvailForDay(d);

          // Availability badge (shown in both modes)
          let availBadge: string | null = null;
          if (avail) {
            if ((avail as any).totalMins != null) {
              const mins = (avail as any).totalMins;
              const h = Math.floor(mins / 60); const m = mins % 60;
              availBadge = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
            } else {
              availBadge = timeDiff(avail.start, avail.end);
            }
          }

          // Booked badge (only in booked mode)
          let bookedBadge: string | null = null;
          if (calMode === 'booked' && dayBookings.length > 0) {
            let total = 0;
            dayBookings.forEach(b => {
              const [sh, sm] = b.startTime.split(':').map(Number);
              const [eh, em] = b.endTime.split(':').map(Number);
              total += (eh * 60 + em) - (sh * 60 + sm);
            });
            const h = Math.floor(total / 60); const m = total % 60;
            bookedBadge = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
          }

          return (
            <div
              key={i}
              className={`min-h-[100px] border-r border-t border-slate-200 p-1.5 ${i % 7 === 6 ? 'border-r-0' : ''} ${isToday ? 'bg-blue-50/40' : past ? 'bg-slate-50/70' : 'bg-white'}`}
            >
              {/* Date number (top-left) + badges (top-right) */}
              <div className="flex items-start justify-between mb-1">
                <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${isToday ? 'bg-blue-600 text-white' : past ? 'text-slate-300' : 'text-slate-700'}`}>
                  {d}
                </span>
                <div className="flex items-center gap-1">
                  {calMode === 'availability' && availBadge && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {availBadge}
                    </span>
                  )}
                  {calMode === 'booked' && bookedBadge && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700">
                      {bookedBadge}
                    </span>
                  )}
                </div>
              </div>

              {/* Availability pills (only in availability mode) */}
              {calMode === 'availability' && avail && (() => {
                const dateOverrideList = overridesByDate[ds];
                if (dateOverrideList?.length) {
                  return dateOverrideList.map((o, oi) => (
                    <div key={oi} className={`${past ? 'bg-blue-300' : 'bg-blue-500'} text-white text-[10px] rounded-md px-1.5 py-0.5 truncate w-full mb-0.5`}>
                      {formatTime12(o.startTime)} – {formatTime12(o.endTime)}
                    </div>
                  ));
                }
                return (
                  <div className={`${past ? 'bg-blue-300' : 'bg-blue-500'} text-white text-[10px] rounded-md px-1.5 py-0.5 truncate w-full mb-0.5`}>
                    {formatTime12(avail.start)} – {formatTime12(avail.end)}
                  </div>
                );
              })()}

              {/* Booking pills (only in booked mode) — show time + event name + guest */}
              {calMode === 'booked' && dayBookings.map(b => (
                <button
                  key={b.id}
                  onClick={e => { e.stopPropagation(); setPopup({ booking: b, x: e.clientX, y: e.clientY }); }}
                  className="w-full text-left bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] rounded-md px-1.5 py-1 mb-0.5 transition-colors"
                  style={{ display: 'block' }}
                >
                  <span className="opacity-90 truncate block">{formatTime12(b.startTime)} – {formatTime12(b.endTime)}</span>
                  <span className="font-semibold truncate block">{b.event.name}</span>
                  <span className="opacity-80 truncate block">{b.guestName}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Booking detail popup */}
      {popup && (
        <div
          className="fixed bg-white rounded-xl shadow-2xl border border-slate-200 p-4"
          style={{ ...getPopupStyle(popup.x, popup.y), zIndex: 9999, width: 280, maxWidth: 280 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start gap-2 mb-3">
            <span
              className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0"
              style={{ backgroundColor: popup.booking.event.color }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 leading-tight">{popup.booking.event.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{popup.booking.guestName}</p>
            </div>
            <button
              onClick={() => setPopup(null)}
              className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Details */}
          <div className="space-y-1.5 text-xs">
            <Row2 label="Time" value={`${formatTime12(popup.booking.startTime)} – ${formatTime12(popup.booking.endTime)}`} />
            <Row2 label="Date" value={popup.booking.date} />
            <Row2 label="Duration" value={`${popup.booking.event.duration} min`} />
            <Row2 label="Timezone" value={popup.booking.timezone} truncate />
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Status</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                popup.booking.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700'
                : popup.booking.status === 'CANCELLED' ? 'bg-red-100 text-red-600'
                : 'bg-slate-100 text-slate-600'
              }`}>
                {popup.booking.status}
              </span>
            </div>
            {popup.booking.guestEmail && (
              <Row2 label="Email" value={popup.booking.guestEmail} truncate />
            )}
          </div>

          {/* Meeting-link section — three cases:
              1. meetingLink present → Join Meeting button (blue).
              2. meetingLink null AND event was configured for auto
                 (zoom_auto / google_meet_auto) → amber "Try again" panel
                 so the admin can retry generation without recreating the
                 booking. Fires POST /availability/bookings/:id/regenerate-meeting-link.
              3. Otherwise → nothing (the booking never had auto-gen). */}
          {popup.booking.meetingLink ? (
            <a
              href={popup.booking.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <Video className="h-3.5 w-3.5" />
              Join Meeting
            </a>
          ) : (popup.booking.event?.meetingProvider === 'zoom_auto' ||
                popup.booking.event?.meetingProvider === 'google_meet_auto') ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
              <p className="font-semibold text-amber-800 mb-1">
                Meeting link couldn't be created
              </p>
              <p className="text-amber-700 mb-2 leading-snug">
                {popup.booking.event.meetingProvider === 'zoom_auto'
                  ? 'Zoom auto-generation failed at booking time — usually a disconnected or expired Zoom integration.'
                  : 'Google Meet auto-generation failed at booking time — usually a disconnected or expired Google Calendar integration.'}
              </p>
              <button
                type="button"
                onClick={regenerateMeetingLink}
                disabled={regenerating}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-md disabled:opacity-60"
              >
                {regenerating ? 'Creating…' : 'Try again'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Row2({ label, value, truncate }: { label: string; value: string; truncate?: boolean }) {
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-slate-400 flex-shrink-0">{label}</span>
      <span className={`text-slate-700 ${truncate ? 'truncate max-w-[160px]' : ''}`}>{value}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function TimeAvailabilityPage() {
  const [searchParams] = useSearchParams();
  const { user: me } = useAuth();
  const targetUserId = searchParams.get('userId');
  const myUserId = me?.id ?? localStorage.getItem('userId') ?? '';
  const userId = targetUserId ?? myUserId;
  const isAdminView = !!(targetUserId && targetUserId !== myUserId);
  const [targetUserName, setTargetUserName] = useState<string | null>(null);

  const [events, setEvents] = useState<AvailEvent[]>([]);
  const [weekly, setWeekly] = useState<DayHours[]>(DEFAULT_WEEKLY);
  const [overrides, setOverrides] = useState<DateOverride[]>([]);
  const [availData, setAvailData] = useState<any>(null);
  const [savingWeekly, setSavingWeekly] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [drawer, setDrawer] = useState<Partial<AvailEvent> | null | false>(false);
  const [activeEventFilter, setActiveEventFilter] = useState<string>('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);

    // Fire all required requests in parallel instead of sequentially — the
    // three endpoints are independent and previously took 3x as long as
    // needed, causing the event cards to flash in well after the page header.
    const eventsReq = api
      .get(`/availability/events${targetUserId ? `?userId=${targetUserId}` : ''}`)
      .catch(() => null);

    const availReq = (isAdminView && targetUserId)
      ? api.get(`/availability/user/${targetUserId}`).catch(() => null)
      : api.get('/availability/me').catch(() => null);

    const calReq = !isAdminView
      ? api.get('/availability/calendar-availability').catch(() => null)
      : Promise.resolve(null);

    const [evRes, availRes, calRes] = await Promise.all([eventsReq, availReq, calReq]);

    if (evRes) setEvents(evRes.data ?? []);

    let avail: any = null;
    if (availRes) {
      if (isAdminView && targetUserId) {
        setTargetUserName(availRes.data?.user?.name ?? null);
        avail = availRes.data?.availability ?? null;
        if (avail) setAvailData(avail); // calendar data comes from same response
      } else {
        avail = availRes.data;
      }
    }

    if (avail) {
      const wd: number[] = avail.workingDays ?? [1, 2, 3, 4, 5];
      const wh: Record<string, { start: string; end: string }[]> = avail.weeklyHours ?? {};
      setWeekly(Array.from({ length: 7 }, (_, i) => {
        const dayRanges = wh[String(i)];
        if (dayRanges?.length) return { enabled: true, ranges: dayRanges };
        const enabled = wd.includes(i);
        return {
          enabled,
          ranges: enabled ? [{ start: avail.startTime ?? '09:00', end: avail.endTime ?? '18:00' }] : [],
        };
      }));
      setOverrides(avail.customOverrides ?? []);
    }

    if (calRes) setAvailData(calRes.data);

    setLoading(false);
  }, [targetUserId, isAdminView]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Events CRUD ─────────────────────────────────────────────────────────────

  const saveEvent = async (form: any) => {
    const editing = drawer && (drawer as AvailEvent).id;
    try {
      if (editing) {
        await api.patch(`/availability/events/${(drawer as AvailEvent).id}`, form);
        toast('success', 'Event updated');
      } else {
        const payload = isAdminView && targetUserId
          ? { ...form, userId: targetUserId }
          : form;
        await api.post('/availability/events', payload);
        toast('success', 'Event created');
      }
      setDrawer(false);
      loadAll();
    } catch {
      toast('error', 'Failed to save event');
    }
  };

  const deleteEvent = async () => {
    if (!drawer || !(drawer as AvailEvent).id) return;
    try {
      await api.delete(`/availability/events/${(drawer as AvailEvent).id}`);
      toast('success', 'Event deleted');
      setDrawer(false);
      loadAll();
    } catch {
      toast('error', 'Failed to delete event');
    }
  };

  const toggleEvent = async (ev: AvailEvent) => {
    try {
      await api.patch(`/availability/events/${ev.id}`, { isActive: !ev.isActive });
      loadAll();
    } catch {
      toast('error', 'Failed to update event');
    }
  };

  const copyLink = (ev: AvailEvent) => {
    // Booking link MUST point to the event OWNER's userId, not the
    // currently-viewing user's — otherwise on shared events (visible via
    // the hierarchy rule) the URL carries the viewer's id, the public
    // /book/:userId endpoint filters strictly by userId and returns []
    // → "Booking link not found" on the guest-facing page. Fall back to
    // the viewing userId only for legacy events that predate ownerId
    // being included in the response.
    const ownerId = ev.userId || userId;
    const url = getBookingUrl(ownerId, ev.slug);
    navigator.clipboard.writeText(url);
    toast('success', 'Booking link copied!');
  };

  // ── Availability save ────────────────────────────────────────────────────────

  const saveWeekly = async () => {
    setSavingWeekly(true);
    try {
      const { workingDays, startTime, endTime, weeklyHours } = weeklyHoursToAvailSettings(weekly);
      const endpoint = isAdminView && targetUserId
        ? `/availability/user/${targetUserId}`
        : '/availability';
      const res = await api.post(endpoint, { workingDays, startTime, endTime, weeklyHours });

      // Update weekly state directly from the returned upserted record
      const saved = res.data;
      if (saved) {
        const wd: number[] = saved.workingDays ?? [1, 2, 3, 4, 5];
        const wh: Record<string, { start: string; end: string }[]> = saved.weeklyHours ?? {};
        setWeekly(Array.from({ length: 7 }, (_, i) => {
          const dayRanges = wh[String(i)];
          if (dayRanges?.length) return { enabled: true, ranges: dayRanges };
          const enabled = wd.includes(i);
          return {
            enabled,
            ranges: enabled ? [{ start: saved.startTime ?? '09:00', end: saved.endTime ?? '18:00' }] : [],
          };
        }));
      }

      // Refresh calendar data
      try {
        if (isAdminView && targetUserId) {
          const adminRes = await api.get(`/availability/user/${targetUserId}`);
          if (adminRes.data?.availability) setAvailData(adminRes.data.availability);
        } else {
          const calRes = await api.get('/availability/calendar-availability');
          setAvailData(calRes.data);
        }
      } catch { /* ok */ }

      toast('success', 'Availability saved');
    } catch {
      toast('error', 'Failed to save availability');
    } finally {
      setSavingWeekly(false);
    }
  };

  // ── Overrides ────────────────────────────────────────────────────────────────

  const addOverride = async (dto: { date: string; timeRanges: { start: string; end: string }[]; reason?: string }) => {
    try {
      const endpoint = isAdminView && targetUserId
        ? `/availability/user/${targetUserId}/overrides`
        : '/availability/overrides';
      await Promise.all(dto.timeRanges.map(r =>
        api.post(endpoint, { date: dto.date, startTime: r.start, endTime: r.end, reason: dto.reason })
      ));
      toast('success', 'Date override added');
      loadAll();
    } catch {
      toast('error', 'Failed to add override');
    }
  };

  const removeOverride = async (id: string) => {
    try {
      const endpoint = isAdminView && targetUserId
        ? `/availability/user/${targetUserId}/overrides/${id}`
        : `/availability/overrides/${id}`;
      await api.delete(endpoint);
      toast('success', 'Override removed');
      loadAll();
    } catch {
      toast('error', 'Failed to remove override');
    }
  };

  // ── Availability dropdown label ───────────────────────────────────────────

  const activeEvents = events.filter(e => e.isActive);
  const filterLabel = activeEventFilter === 'all'
    ? `Active on ${activeEvents.length} event type${activeEvents.length !== 1 ? 's' : ''}`
    : (events.find(e => e.id === activeEventFilter)?.name ?? 'Select event');

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isAdminView && targetUserName ? `${targetUserName}'s Availability` : 'Time Availability'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdminView
              ? `Viewing and editing on behalf of ${targetUserName ?? 'user'}`
              : 'Manage your booking schedule and event types'}
          </p>
        </div>

        {/* ── SECTION 1: Event Types ───────────────────────────────────────────── */}
        <section>
          <h2 className="text-base font-bold text-slate-800 mb-4">Event Types</h2>

          {/* Horizontal scroll row */}
          <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' } as React.CSSProperties}>
            {loading ? (
              // Skeleton placeholders while events load — prevents UI flash
              <>
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-56 bg-white rounded-xl border border-slate-200 overflow-hidden min-h-[140px] animate-pulse"
                  >
                    <div className="h-1.5 w-full bg-slate-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-slate-200 rounded w-2/3" />
                      <div className="h-3 bg-slate-100 rounded w-1/2" />
                      <div className="h-3 bg-slate-100 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {events.map(ev => (
                  <EventCard
                    key={ev.id}
                    event={ev}
                    userId={userId}
                    currentUserId={myUserId}
                    onClick={() => setDrawer(ev)}
                    onToggle={() => toggleEvent(ev)}
                    onCopyLink={() => copyLink(ev)}
                    isAdminView={isAdminView}
                  />
                ))}

                {/* New Event card */}
                <button
                  onClick={() => setDrawer({})}
                  className="flex-shrink-0 w-56 bg-white rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50/30 transition-colors flex flex-col items-center justify-center gap-2 min-h-[140px] cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Plus className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-blue-600">New Event Type</span>
                </button>
              </>
            )}
          </div>
        </section>

        {/* ── SECTION 2: Availability ──────────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-base font-bold text-slate-800">Availability</h2>

            <div className="flex items-center gap-3">
              {/* Active event dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(s => !s)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <span>{filterLabel}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-20">
                    <button
                      onClick={() => { setActiveEventFilter('all'); setDropdownOpen(false); }}
                      className="flex items-center justify-between w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      All event types
                      {activeEventFilter === 'all' && <Check className="h-3.5 w-3.5 text-blue-600" />}
                    </button>
                    {events.map(ev => (
                      <button key={ev.id}
                        onClick={() => { setActiveEventFilter(ev.id); setDropdownOpen(false); }}
                        className="flex items-center justify-between w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ev.color }} />
                          {ev.name}
                        </span>
                        {activeEventFilter === ev.id && <Check className="h-3.5 w-3.5 text-blue-600" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* View toggle */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <LayoutList className="h-3.5 w-3.5" /> List
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'calendar' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <CalendarDays className="h-3.5 w-3.5" /> Calendar
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'list' ? (
            <ListView
              weekly={weekly}
              setWeekly={setWeekly}
              overrides={overrides}
              onAddOverride={addOverride}
              onRemoveOverride={removeOverride}
              onSaveWeekly={saveWeekly}
              saving={savingWeekly}
            />
          ) : (
            <CalendarView userId={userId} events={events} availData={availData} />
          )}
        </section>
      </div>

      {/* Event type drawer */}
      {drawer !== false && (
        <EventDrawer
          event={drawer}
          onClose={() => setDrawer(false)}
          onSave={saveEvent}
          onDelete={(drawer as AvailEvent)?.id ? deleteEvent : undefined}
          isAdminView={isAdminView}
          isOwnerOrAdmin={me?.role === 'admin'}
          readOnly={!!((drawer as AvailEvent)?.isOwnerEvent && (drawer as AvailEvent)?.userId !== myUserId)}
        />
      )}
    </DashboardLayout>
  );
}
